/**
 * Digital certificate / public QR / score-breakdown config.
 * Usage: see ./README.md in this folder.
 */

const DEFAULTS = {
    digitalCertificate: true,
    scoreBreakdown: true,
    certificateQrColor: '#E90046',
    transcriptQrColor: '#E90046',
    ifoLayout: false,
    advisorDigitalCertificate: false,
    advisorCertificateQrColor: '#E90046',
    awardCertificateQrColor: '#E90046',
    publicHost: 'https://my.eae.international'
}

export const DIGITAL_CERTIFICATE_PROJECTS = {
    '2026IPsyO': {
        slug: 'ipsyo',
        downloadPrefix: '2026_IPsyO',
        advisorDigitalCertificate: true
    },
    '2026IPsyO_Final': {
        slug: 'ipsyo',
        downloadPrefix: '2026_IPsyO_Final'
    },
    '2026IBO': {
        slug: 'ibo',
        downloadPrefix: '2026_IBO',
        certificateQrColor: '#B77900',
        transcriptQrColor: '#1D68A9',
        advisorDigitalCertificate: true,
        advisorCertificateQrColor: '#1D68A9',
        awardCertificateQrColor: '#B77900'
    },
    '2026IBO_Final': {
        slug: 'ibo',
        downloadPrefix: '2026_IBO_Final',
        certificateQrColor: '#B77900',
        transcriptQrColor: '#1D68A9',
        advisorDigitalCertificate: true,
        advisorCertificateQrColor: '#1D68A9',
        awardCertificateQrColor: '#B77900'
    },
    '2026WEC': {
        slug: 'wec',
        downloadPrefix: '2026_WEC',
        certificateQrColor: '#D3A12A',
        transcriptQrColor: '#D3A12A'
    },
    '2026WEC_Continental': {
        slug: 'wec',
        downloadPrefix: '2026_WEC_Continental',
        certificateQrColor: '#D3A12A',
        transcriptQrColor: '#D3A12A'
    },
    '2026WEC_Final': {
        slug: 'wec',
        downloadPrefix: '2026_WEC_Final',
        certificateQrColor: '#D3A12A',
        transcriptQrColor: '#D3A12A',
        // Final Score = weighted sum of module scores (not a simple total)
        scoreWeights: {
            'Continental Total': 0.4,
            'Team Essay': 0.6
        },
        scoreWeightNote: '*The final score is weighted as 40% Continental Total and 60% Team Essay.'
    },
    '2026Biothon': {
        slug: 'biothon',
        downloadPrefix: '2026_Biothon',
        certificateQrColor: '#D5981E',
        transcriptQrColor: '#D5981E',
        // Biothon currently shows QR but not score-breakdown penalties
        scoreBreakdown: false
    },
    '2026IEnvO': {
        slug: 'ienvo',
        downloadPrefix: '2026_IEnvO',
        certificateQrColor: '#309139',
        transcriptQrColor: '#309139'
    },
    '2026IEnvO_Final': {
        slug: 'ienvo',
        downloadPrefix: '2026_IEnvO_Final',
        certificateQrColor: '#309139',
        transcriptQrColor: '#309139'
    },
    '2026Chemithon': {
        slug: 'chemithon',
        downloadPrefix: '2026_Chemithon',
        certificateQrColor: '#D5981E',
        transcriptQrColor: '#D5981E'
    },
    '2026Inforthon': {
        slug: 'inforthon',
        downloadPrefix: '2026_Inforthon',
        certificateQrColor: '#FF8C00',
        transcriptQrColor: '#FF8C00'
    },
    '2026IFO': {
        slug: 'ifo',
        downloadPrefix: '2026_IFO',
        certificateQrColor: '#A68B00',
        transcriptQrColor: '#A68B00',
        ifoLayout: true
    },
    '2026IFO_Final': {
        slug: 'ifo',
        downloadPrefix: '2026_IFO_Final',
        certificateQrColor: '#A68B00',
        transcriptQrColor: '#A68B00',
        ifoLayout: true
    }
}

export function getDigitalCertificateConfig(projectCode) {
    if (!projectCode || !DIGITAL_CERTIFICATE_PROJECTS[projectCode]) {
        return null
    }
    return Object.assign({}, DEFAULTS, DIGITAL_CERTIFICATE_PROJECTS[projectCode])
}

export function supportsDigitalCertificate(projectCode) {
    const config = getDigitalCertificateConfig(projectCode)
    return !!(config && config.digitalCertificate)
}

export function supportsScoreBreakdown(projectCode) {
    const config = getDigitalCertificateConfig(projectCode)
    return !!(config && config.scoreBreakdown)
}

export function getCertificateDownloadPrefix(projectCode) {
    const config = getDigitalCertificateConfig(projectCode)
    return config ? config.downloadPrefix : null
}

export function buildPublicStudentCertificateUrl(projectCode, applicationId, tab) {
    const config = getDigitalCertificateConfig(projectCode)
    if (!config || !applicationId) {
        return ''
    }
    return config.publicHost + '/#/certificate/' + config.slug + '/student?id=' +
        encodeURIComponent(applicationId) + '&tab=' + encodeURIComponent(tab || 'certificate')
}

export function buildPublicAdvisorCertificateUrl(projectCode, applicationId, tab, advisorId) {
    const config = getDigitalCertificateConfig(projectCode)
    if (!config || !applicationId) {
        return ''
    }
    let url = config.publicHost + '/#/certificate/' + config.slug + '/advisor?id=' +
        encodeURIComponent(applicationId) + '&tab=' + encodeURIComponent(tab || 'certificate')
    if (advisorId) {
        url += '&advisor_id=' + encodeURIComponent(advisorId)
    }
    return url
}

export function supportsAdvisorDigitalCertificate(projectCode) {
    const config = getDigitalCertificateConfig(projectCode)
    return !!(config && config.advisorDigitalCertificate)
}

export function isPublicStudentCertificatePath(path) {
    return /^\/certificate\/[^/]+\/student$/.test(path || '')
}

export function isPublicAdvisorCertificatePath(path) {
    return /^\/certificate\/[^/]+\/advisor$/.test(path || '')
}

export function listDigitalCertificateSlugs() {
    const slugs = {}
    Object.keys(DIGITAL_CERTIFICATE_PROJECTS).forEach((code) => {
        const slug = DIGITAL_CERTIFICATE_PROJECTS[code].slug
        if (slug) {
            slugs[slug] = true
        }
    })
    return Object.keys(slugs)
}
